/**
 * La conversación con un colaborador.
 *
 * Ya no es «el chat» con un desplegable de a quién le hablas: es el hilo que tienes abierto con
 * una persona concreta, elegida en la barra de la izquierda, y que sigue ahí mañana. El cambio no
 * es de forma: un desplegable convierte a dieciocho oficios en un parámetro, y un hilo por
 * colaborador es lo que hace que el corrector recuerde lo que le dijiste la semana pasada.
 *
 * El hilo se guarda en la carpeta del manuscrito y es EL MISMO que usa el panel de Word. Lo que le
 * cuentes aquí lo sabe allí.
 *
 * Y sigue en pie lo de siempre: el chat DELIBERA. Al manuscrito solo se escribe desde «Aplicar»,
 * que es otra decisión y deja otra huella.
 */
import { useEffect, useRef, useState } from "react";
import TextoChat from "./TextoChat";
import { useUser } from "@clerk/react";
import Cara from "./Cara";
import { AMBITOS, SinSuscripcion, anexar, capitulosDeTexto, catalogoModelos, conversar, decidir, dolares, encargar, hilo as leerHilo, miles, modelosAsignados, olvidar, subir } from "../api";
import type { Ambito, Capitulo, Colaborador, Fichero, ModeloCatalogo, Telemetria } from "../api";
import { CARACTERES_POR_ADJUNTO, PALABRAS_MAX_POR_ADJUNTO, palabrasDe, caminoDelChat, estimarCoste, frasesDeConsultas, inicialesDe, nombreDeRuta, ordenarPorElegido, palabrasQueViajan, recortesAlConversar } from "../logica";
import type { Consulta, Propuesta } from "../logica";
import { PropuestaEncargo } from "./PropuestaEncargo";
import { useFlujo } from "../flujo";

interface Turno {
  mio: boolean;
  texto: string;
  at?: number;
  // `op` es el identificador que devuelve el motor. Sin él, «me lo quedo» sería una opinión;
  // con él, es una línea en la bitácora que el certificado puede citar.
  op?: string | null;
  decidido?: string;
  // El texto exacto que se mandó a reescribir (el capítulo o el documento). «Aplicar» compara la
  // propuesta con ESTO: compararla con el libro entero borraría todo lo que no es el capítulo.
  base?: string;
  // Lo que el colaborador miró del libro para contestar (CH-4).
  consultas?: Consulta[];
  // Las fichas de encargo que dejó (CH-4 fase B): nada se lanza sin «Hazlo».
  propuestas?: Propuesta[];
}

export function useChat(
  equipo: Colaborador[], alGastar: () => void, carpeta: string,
  abierto: { ruta: string; texto: string } | null,
  alCambiarBandeja: () => void,
  // Decidir no cuesta tokens, así que la telemetría no se mueve y las pantallas que colgaban de
  // ella se quedaban con la cifra vieja: el autor pulsaba «me lo quedo» y el panel seguía diciendo
  // «aún no has decidido nada». Este aviso es el que lo arregla.
  alPasarAlgo: () => void = () => {},
) {
  const [adjuntos, setAdjuntos] = useState<string[]>([]);
  // El modelo de ESTE encargo. `null` = el del agente (sección Modelos). Vale para un envío y vuelve solo.
  const [modelo, setModelo] = useState<string | null>(null);
  // ALCANCE (Acciones): `null` = todo el documento abierto. Elegido un capítulo, se queda puesto
  // entre envíos — a diferencia del modelo, «trabajo sobre el capítulo 22» no es de un solo turno,
  // es de toda la sesión, igual que los adjuntos.
  const [capituloElegido, setCapitulo] = useState<{ title: string; texto: string } | null>(null);
  const capitulo = capituloElegido;
  // R-18: el documento abierto viaja entero (hasta 500.000 caracteres) en vez de sus primeras 1.450
  // palabras. Cuesta más y se dice antes; se queda puesto entre envíos, como el alcance.
  const [libroEntero, setLibroEntero] = useState(false);
  const [elegido, setElegido] = useState("");
  const [encargo, setEncargo] = useState("");
  // El Studio como flujo: el coach es el único interlocutor y propone el trabajo de cualquier oficio.
  const flujo = useFlujo();
  const [hilo, setHilo] = useState<Turno[]>([]);
  const [cargando, setCargando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [sinSuscripcion, setSinSuscripcion] = useState(false);

  const colaborador = equipo.find((c) => c.id === elegido);

  // Al abrir a alguien se trae lo que ya hablasteis. Mientras llega, el hilo se vacía: enseñar la
  // conversación anterior bajo el nombre del siguiente sería atribuirle a alguien lo que dijo otro.
  useEffect(() => {
    if (!elegido) { setHilo([]); return; }
    let vigente = true;
    setCargando(true);
    setHilo([]);
    setFallo(null);
    leerHilo(elegido, carpeta)
      .then((ms) => {
        if (!vigente) return;
        setHilo(ms.map((m) => ({
          mio: m.role === "user", texto: m.content, at: m.at, op: m.op ?? null, consultas: m.consultas, propuestas: m.propuestas,
        })));
      })
      .catch(() => { if (vigente) setFallo("No se ha podido leer la conversación guardada."); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [elegido, carpeta]);

  // Un capítulo elegido lo es DE un documento concreto: si el autor abre otro, el alcance de
  // antes ya no significa nada — mantenerlo puesto encargaría en silencio sobre el libro que no toca.
  useEffect(() => { setCapitulo(null); }, [abierto?.ruta]);

  /**
   * Enviar lo escrito en la caja, o —desde una etapa— un encargo concreto con su alcance. Las
   * opciones existen para que «Corregir el capítulo 3» se mande de una vez, sin depender de que el
   * estado de la caja se haya actualizado.
   */
  async function enviar(opciones?: { agente?: string; texto?: string; capitulo?: { title: string; texto: string } | null }) {
    const colaborador = opciones?.agente ? equipo.find((c) => c.id === opciones.agente) : equipo.find((c) => c.id === elegido);
    const capitulo = opciones && "capitulo" in opciones ? opciones.capitulo ?? null : capituloElegido;
    const texto = opciones?.texto ?? encargo;
    if (!colaborador || !texto.trim() || ocupado) return;
    // Sin manuscrito no se encarga nada: el entregable no tendría dónde vivir.
    if (!carpeta) { setFallo("Elige antes la carpeta de tu manuscrito."); return; }
    // Reescribir sin texto sobre el que trabajar reescribía TU MENSAJE (César, 16/17-sep: el
    // corrector le devolvía «Quiero corregir el manuscrito…» corregido). Se para y se dice.
    if (caminoDelChat(colaborador.output) === "reescritura" && !capitulo && !abierto) {
      setFallo("Elige antes tu manuscrito en Proyectos: el corrector necesita el texto que tiene que corregir.");
      return;
    }
    // Un libro entero no cabe en UNA respuesta del modelo: la reescritura se cortaría a mitad. Para eso
    // está la corrección del manuscrito entero, que trabaja por partes en segundo plano.
    if (caminoDelChat(colaborador.output) === "reescritura" && !capitulo && abierto && palabrasDe(abierto.texto) > 4000) {
      setFallo("El manuscrito entero no cabe en una sola respuesta. Ve a la etapa Corrección → «Corregir el manuscrito entero» (lo hace por partes y crea una versión con los cambios marcados), o elige un capítulo en «Alcance».");
      return;
    }
    const mio = texto.trim();
    setHilo((h) => [...h, { mio: true, texto: capitulo ? `${mio}\n(Alcance: ${capitulo.title})` : mio, at: Date.now() }]);
    if (!opciones?.texto) setEncargo("");
    setOcupado(true);
    setFallo(null);
    try {
      // Los que no reescriben CONVERSAN y contestan aquí. Hasta el 15-sep todo lo que se le decía al
      // coach salía como un Word, «dame 3 alternativas para el título» incluido. El motor guarda
      // los dos turnos en el hilo, así que no se anexan desde aquí.
      if (caminoDelChat(colaborador.output) === "conversacion") {
        const contexto = [...new Set([...adjuntos, ...(abierto ? [abierto.ruta] : [])])];
        const mensaje = capitulo
          ? `${mio}\n\n===== ${capitulo.title} =====\n${capitulo.texto.slice(0, CARACTERES_POR_ADJUNTO)}`
          : mio;
        const respuesta = await conversar(colaborador, mensaje, carpeta, contexto, modelo, hilo.length, libroEntero, abierto?.ruta ?? null, flujo);
        setHilo((h) => [...h, { mio: false, texto: respuesta.texto, at: Date.now(), consultas: respuesta.consultas, propuestas: respuesta.propuestas }]);
        alGastar();
        alCambiarBandeja();
        return;
      }
      // Con alcance elegido, el colaborador trabaja SOLO sobre ese capítulo. Si no, sobre el
      // documento abierto entero. Sin documento, lo escrito es las dos cosas — de siempre.
      const base = capitulo?.texto ?? abierto?.texto ?? mio;
      const r = await encargar(colaborador, mio, base, carpeta, adjuntos, modelo);
      const dicho = r.clase === "texto"
        ? r.texto
        : `Entregable listo: ${miles(r.caracteres)} caracteres.` +
          (r.ruta ? `\nGuardado en ${r.ruta}` : "\nNo se pudo guardar en disco; ábrelo desde el proyecto.");
      setHilo((h) => [...h, { mio: false, texto: dicho, at: Date.now(), op: r.op, base: r.clase === "texto" ? base : undefined }]);
      alGastar();   // el gasto de arriba se refresca: la factura se ve en el momento
      alPasarAlgo();  // y con él, el informe y la bitácora: si el encargo ha dejado un
                      // entregable, la sección que lo enseña tiene que enterarse ya.

      // Y queda escrito en la carpeta del autor. Si esto falla, lo que se ve en pantalla sigue
      // ahí: lo que se pierde es el recuerdo de mañana, y por eso se dice en vez de callarlo.
      try {
        await anexar(colaborador.id, carpeta, [
          { role: "user", content: mio },
          { role: "assistant", content: dicho, ...(r.op ? { op: r.op } : {}) },
        ]);
        alCambiarBandeja();
      } catch {
        setFallo("La respuesta está aquí, pero no se ha podido guardar en la conversación.");
      }
    } catch (e) {
      // La puerta del producto no es un error: se ofrece suscribirse, no se pinta un mensaje rojo.
      if (e instanceof SinSuscripcion) setSinSuscripcion(true);
      else setFallo(e instanceof Error ? e.message : "No se pudo completar el encargo.");
    } finally {
      setOcupado(false);
      setModelo(null);   // el cambio era para este encargo
    }
  }

  /**
   * Lo que el autor hace con la propuesta.
   *
   * Se pregunta AQUÍ, en el momento, porque una decisión que se pide después ya no se acuerda —y
   * porque lo que hoy sostiene el certificado es una heurística que busca el texto en el
   * manuscrito y da por conservado lo que encuentre. Esto es lo mismo, pero sabido.
   */
  async function decidirTurno(i: number, d: "aceptado" | "editado" | "descartado", ambitos?: Ambito[]) {
    const t = hilo[i];
    if (!t?.op) return;
    setHilo((h) => h.map((x, n) => (n === i ? { ...x, decidido: d } : x)));
    try {
      await decidir(t.op, d, carpeta, abierto?.ruta, ambitos);
      alPasarAlgo();
    } catch {
      setHilo((h) => h.map((x, n) => (n === i ? { ...x, decidido: undefined } : x)));
      setFallo("No se pudo registrar la decisión.");
    }
  }

  /** Borrar la conversación es del autor y de nadie más: es su texto y su registro. */
  async function olvidarHilo() {
    if (!colaborador) return;
    try {
      await olvidar(colaborador.id, carpeta);
      setHilo([]);
      alCambiarBandeja();
    } catch {
      setFallo("No se ha podido borrar la conversación.");
    }
  }

  // Una ficha que el autor ha encargado o descartado: se cambia en el hilo de la pantalla (el motor ya lo apunta).
  const cambiarPropuesta = (nueva: Propuesta) => setHilo((h) => h.map((t) => (
    t.propuestas?.some((p) => p.id === nueva.id) ? { ...t, propuestas: t.propuestas.map((p) => (p.id === nueva.id ? nueva : p)) } : t)));
  return { elegido, setElegido, encargo, setEncargo, hilo, ocupado, cargando, fallo, setFallo, modelo, setModelo,
           capitulo, setCapitulo, libroEntero, setLibroEntero,
           colaborador, enviar, abierto, adjuntos, setAdjuntos, decidirTurno, olvidarHilo,
           sinSuscripcion, setSinSuscripcion, carpeta, cambiarPropuesta, alPasarAlgo };
}

const DECIDIDO: Record<string, string> = {
  aceptado: "Te lo quedaste", editado: "Lo retocaste", descartado: "Lo descartaste",
};

/** El hilo entero, ocupando el lienzo: cuando hablas con alguien, eso es la pantalla. */
export function Conversacion(
  { c, alAplicar }: {
    c: ReturnType<typeof useChat>;
    alAplicar?: (t: { texto: string; op?: string | null; base?: string }) => void;
  },
) {
  const { hilo, ocupado, cargando, colaborador, decidirTurno, olvidarHilo } = c;
  // Las iniciales de quien ha entrado, del mismo dato de la cuenta que enseña la barra lateral.
  const { user } = useUser();
  const yo = inicialesDe({ firstName: user?.firstName, lastName: user?.lastName, email: user?.primaryEmailAddress?.emailAddress });
  // Los ámbitos que el autor ha marcado en una propuesta antes de decidirla: «esto cambia el
  // final». Viajan con la decisión (AWAP 2 §5.2 w1) y se preguntan aquí porque después ya no se
  // acuerda nadie de si aquel cambio tocaba la trama.
  const [fondo, setFondo] = useState<Record<number, Ambito[]>>({});
  const marcar = (i: number, a: Ambito) => setFondo((f) => {
    const ya = f[i] ?? [];
    return { ...f, [i]: ya.includes(a) ? ya.filter((x) => x !== a) : [...ya, a] };
  });
  // El final del hilo, a la vista. Abrir una conversación por su primer mensaje obligaría a
  // desplazarse hasta abajo cada vez para encontrar lo último que se dijo.
  const pie = useRef<HTMLDivElement>(null);
  useEffect(() => { pie.current?.scrollIntoView({ block: "end" }); }, [hilo.length, ocupado]);
  if (!colaborador) return null;
  const reescribe = colaborador.output === "rewrite";

  return (
    <section className="carta hilo">
      <div className="quien-hilo">
        <Cara id={colaborador.id} nombre={colaborador.name} />
        <span className="n">
          <b>{colaborador.name}</b>
          <span>{colaborador.phase} · {colaborador.goal}</span>
        </span>
        {hilo.length > 0 && (
          <button className="chip" onClick={olvidarHilo} title="Borra el hilo guardado en tu carpeta">
            Borrar la conversación
          </button>
        )}
      </div>

      {cargando && <p className="vacio">Abriendo lo que ya hablasteis…</p>}

      {!cargando && !hilo.length && (
        <p className="vacio">
          Todavía no le has encargado nada a {colaborador.name.split(" ")[0]}. Se ocupa
          de: {colaborador.goal.charAt(0).toLowerCase() + colaborador.goal.slice(1)}
        </p>
      )}

      <div className="relevo">
        {hilo.map((t, i) => (
          <div className={"paso turno" + (t.mio ? " mio" : "")} key={i}>
            {t.mio
              ? <span className="cara" style={{ background: "var(--azul-suave)", color: "var(--azul)" }}>{yo}</span>
              : <Cara id={colaborador.id} nombre={colaborador.name} />}
            <span className="qu">
              <b>{t.mio ? "Tú" : colaborador.name}</b>
              {t.mio ? <span className="texto-chat">{t.texto}</span> : <TextoChat texto={t.texto} />}
              {!t.mio && t.propuestas?.map((p) => (
                <PropuestaEncargo key={p.id} p={p} agente={colaborador.id} carpeta={c.carpeta} doc={c.abierto?.ruta ?? null}
                                  alCambiar={c.cambiarPropuesta} alLanzar={c.alPasarAlgo} />
              ))}
              {!t.mio && frasesDeConsultas(t.consultas) && <span className="consultas">{frasesDeConsultas(t.consultas)}</span>}
              {!t.mio && t.op && (
                t.decidido
                  ? <span className="grado baja">{DECIDIDO[t.decidido]}</span>
                  : (
                    <span className="decidir">
                      {/* CÉSAR (17-sep): con ocho etiquetas iguales no sabía cuál pulsar ni dónde quedaba el
                          trabajo. Una propuesta de texto tiene UNA acción principal —llevarla al Word— y
                          se dice qué hace; lo demás, en segundo plano. */}
                      {reescribe && alAplicar && t.base ? (
                        <>
                          <span className="vacio">
                            Esto es una propuesta: tu Word no ha cambiado. Para tenerla en el documento, pulsa
                            «Llevar a mi Word»: se crea una versión nueva con los cambios marcados, en la carpeta del proyecto.
                          </span>
                          <span className="botones">
                            <button className="btn" onClick={() => alAplicar({ texto: t.texto, op: t.op, base: t.base })}>Llevar a mi Word</button>
                            <button className="btn texto" onClick={() => decidirTurno(i, "descartado", fondo[i])}>No me sirve</button>
                            <button className="btn texto" onClick={() => decidirTurno(i, "aceptado", fondo[i])}>Me lo quedo sin llevarlo</button>
                          </span>
                          <details className="ambitos">
                            <summary>¿Este cambio toca la historia? (opcional, para tu certificado)</summary>
                            <span className="botones">
                              {AMBITOS.map((a) => (
                                <button key={a.id} className={`chip${fondo[i]?.includes(a.id) ? " puesto" : ""}`} onClick={() => marcar(i, a.id)}>
                                  {fondo[i]?.includes(a.id) ? "✓ " : ""}{a.et}
                                </button>
                              ))}
                            </span>
                          </details>
                        </>
                      ) : (
                        <span className="botones">
                          <button className="btn fantasma" onClick={() => decidirTurno(i, "aceptado", fondo[i])}>Me sirve</button>
                          {reescribe && <button className="btn texto" onClick={() => decidirTurno(i, "editado", fondo[i])}>Lo he retocado</button>}
                          <button className="btn texto" onClick={() => decidirTurno(i, "descartado", fondo[i])}>No me sirve</button>
                        </span>
                      )}
                    </span>
                  )
              )}
            </span>
          </div>
        ))}
        {ocupado && (
          <div className="paso">
            <span className="cara" style={{ background: "var(--sup3)", color: "var(--mut)" }}>··</span>
            <span className="qu"><b>{colaborador.name}</b><span>Trabajando…</span></span>
          </div>
        )}
        <div ref={pie} />
      </div>
    </section>
  );
}

/** El compositor: anclado abajo, y solo cuando hay alguien con quien hablar. */
// «Antes de un encargo LARGO» (DECISIONES.md §5): un mensaje de chat corto no necesita la cifra
// encima — solo el encargo que de verdad puede notarse en la factura.
const PALABRAS_ENCARGO_LARGO = 200;

export function Compositor(
  { c, ficheros, carpeta, doc, tele, alSubir }: {
    c: ReturnType<typeof useChat>; ficheros: Fichero[]; doc?: string | null; tele: Telemetria | null;
    carpeta: string; alSubir: (f: Fichero) => void;
  },
) {
  const { encargo, setEncargo, ocupado, fallo, colaborador, enviar, adjuntos, setAdjuntos } = c;
  // El manuscrito elegido va adjunto por defecto: «del libro que tengo abierto» tiene que llegarle al
  // colaborador sin que el autor sepa qué es adjuntar. Se quita con su pastilla; al cambiar de
  // manuscrito se suelta el anterior.
  const docAnterior = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    setAdjuntos((a) => {
      const sin = docAnterior.current ? a.filter((x) => x !== docAnterior.current) : a;
      return doc && !sin.includes(doc) ? [doc, ...sin] : sin;
    });
    docAnterior.current = doc;
  }, [doc]);
  // El modelo, a la vista junto a «Adjuntar»: el del agente, o el que elijas para este envío.
  const [porAgente, setPorAgente] = useState<Record<string, string>>({});
  const [eligeModelo, setEligeModelo] = useState(false);
  const [catalogo, setCatalogo] = useState<ModeloCatalogo[]>([]);
  const [buscaModelo, setBuscaModelo] = useState("");
  useEffect(() => { modelosAsignados().then((e) => setPorAgente(e.porAgente ?? {})).catch(() => {}); }, [colaborador?.id]);
  // ALCANCE: los capítulos del documento abierto, para poder restringir el encargo a uno solo.
  // Se piden en cuanto se abre un documento —no hace falta esperar a que el autor lo pida— porque
  // es texto que ya tenemos y sirve para decidir si vale la pena enseñar el selector: sin al menos
  // dos capítulos detectados, «alcance» no significa nada y el chip ni aparece.
  const [caps, setCaps] = useState<Capitulo[] | null>(null);
  // 17-sep: con capítulos que no son capítulos (números de una lista), elegir «el capítulo 2» no
  // significa nada. Si el motor dice que no son fiables, no se ofrece el alcance por capítulo.
  const [capsFiables, setCapsFiables] = useState(true);
  const [eligeAlcance, setEligeAlcance] = useState(false);
  useEffect(() => {
    setCaps(null); setCapsFiables(true);
    if (c.abierto?.texto) {
      capitulosDeTexto(c.abierto.texto, carpeta, c.abierto.ruta)
        .then((r) => { setCaps(r.capitulos); setCapsFiables(r.fiable !== false); })
        .catch(() => setCaps([]));
    }
  }, [c.abierto?.ruta]);
  // Lo que de verdad viaja al motor si se pulsa enviar AHORA, con el mismo criterio que `enviar()`.
  const palabrasEncargo = palabrasQueViajan({
    camino: caminoDelChat(colaborador?.output ?? "document"),
    encargo, abiertoTexto: c.abierto?.texto, abiertoRuta: c.abierto?.ruta, capituloTexto: c.capitulo?.texto, adjuntos,
    libroEntero: c.libroEntero,
  });
  const encargoLargo = palabrasEncargo >= PALABRAS_ENCARGO_LARGO;
  useEffect(() => {
    if ((eligeModelo || c.modelo || encargoLargo) && !catalogo.length) {
      catalogoModelos().then((r) => setCatalogo(r.modelos)).catch(() => {});
    }
  }, [eligeModelo, c.modelo, encargoLargo, catalogo.length]);
  const modeloActual = c.modelo ?? (colaborador ? porAgente[colaborador.id] : undefined) ?? "";
  // La línea de DECISIONES.md §5: «esto son ~4 minutos y unos 0,50 $ de tu clave», dicha ANTES de
  // pulsar enviar. Solo si se sabe el precio real del modelo — sin él no se inventa una cifra.
  // Con la caja vacía no hay encargo que estimar (R-05).
  const camino = caminoDelChat(colaborador?.output ?? "document");
  const estimacion = encargoLargo && encargo.trim()
    ? estimarCoste(palabrasEncargo, catalogo.find((m) => m.id === modeloActual),
        colaborador ? tele?.byAgent[colaborador.id] : undefined, camino === "reescritura" ? "reescritura" : "normal", { unaLlamada: true })
    : null;
  const recortes = encargo.trim()
    ? recortesAlConversar({ camino, abiertoTexto: c.abierto?.texto, abiertoRuta: c.abierto?.ruta, adjuntos, libroEntero: c.libroEntero })
    : [];
  const sinPrefijo = (n: string) => n.replace(/^[^:]{1,40}:\s*/, "");
  const nombreModelo = (id: string) => {
    const m = catalogo.find((x) => x.id === id);
    return m ? sinPrefijo(m.nombre) : (id.split("/").pop() ?? id);
  };
  const [eligiendo, setEligiendo] = useState(false);
  const [filtro, setFiltro] = useState("");   // el buscador de adjuntos
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  /** Un fichero que llega arrastrado o elegido: se guarda en el manuscrito y queda adjuntado. */
  async function recibir(lista: FileList | null) {
    if (!lista?.length) return;
    if (!carpeta) { setError("Elige antes la carpeta de tu manuscrito."); return; }
    setSubiendo(true);
    setError(null);
    setAviso(null);
    try {
      const avisos: string[] = [];
      for (const f of Array.from(lista)) {
        const nuevo = await subir(carpeta, f);
        // Lo que el motor hizo con el nombre se dice aquí, en el momento: si no, el autor busca
        // «manuscrito.docx» después y abre el de antes sin entender por qué no está su cambio.
        if (nuevo.renombrado) {
          avisos.push(`Ya había un «${nuevo.pedido}» distinto en la carpeta: el tuyo se ha guardado como «${nuevo.name}» y el anterior sigue intacto.`);
        } else if (nuevo.igual) {
          avisos.push(`«${nuevo.name}» ya estaba en la carpeta, idéntico: no se ha duplicado.`);
        }
        alSubir(nuevo);
        setAdjuntos((a) => (a.includes(nuevo.path) ? a : [...a, nuevo.path]));
      }
      if (avisos.length) setAviso(avisos.join(" "));
      setEligiendo(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo subir el documento.");
    } finally {
      setSubiendo(false);
    }
  }

  const alternar = (ruta: string) =>
    setAdjuntos((a) => (a.includes(ruta) ? a.filter((x) => x !== ruta) : [...a, ruta]));

  return (
      <div className="compositor">
        <div
          className="caja"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); recibir(e.dataTransfer.files); }}
        >
          {(fallo || error) && (
            <p className="vacio" style={{ color: "var(--acc)" }}>{fallo || error}</p>
          )}
          {/* Lo que la subida hizo con el nombre: no es un error, así que no va en rojo. */}
          {aviso && <p className="vacio">{aviso}</p>}

          {eligiendo && (
            <div className="adjuntos">
              {ficheros.length === 0 && (
                <span className="vacio">
                  Todavía no hay documentos. Arrastra uno aquí o pulsa «Subir documento».
                </span>
              )}
              {/* NO SE PINTA LA CARPETA ENTERA. Con Descargas de proyecto salían cientos de pastillas
                  (11-sep: «cuando quieres subir un doc te aparece mi lista»). Se enseñan el
                  manuscrito elegido y lo ya adjuntado; el resto se busca por nombre, ocho como
                  mucho, que es lo que hace cualquier selector de adjuntos. */}
              {ordenarPorElegido(ficheros, doc)
                .filter((f) => f.path === doc || adjuntos.includes(f.path))
                .map((f) => (
                  <button key={f.path} title={f.name} className={"chip" + (adjuntos.includes(f.path) ? " puesto" : "")} onClick={() => alternar(f.path)}>
                    {adjuntos.includes(f.path) ? "✓ " : ""}{f.name}
                  </button>
                ))}
              {ficheros.length > 1 && (
                <input
                  className="escribir" style={{ minWidth: 220 }}
                  placeholder={`Buscar entre ${ficheros.length} documentos…`}
                  value={filtro} onChange={(e) => setFiltro(e.target.value)}
                />
              )}
              {filtro.trim() && ficheros
                .filter((f) => f.path !== doc && !adjuntos.includes(f.path) && f.name.toLowerCase().includes(filtro.trim().toLowerCase()))
                .slice(0, 8)
                .map((f) => (
                  <button key={f.path} className="chip" onClick={() => { alternar(f.path); setFiltro(""); }}>{f.name}</button>
                ))}
            </div>
          )}
          {eligeModelo && (
            <div className="adjuntos">
              <input
                className="escribir" autoFocus style={{ minWidth: 220 }}
                placeholder="Busca un modelo para este encargo…"
                value={buscaModelo} onChange={(e) => setBuscaModelo(e.target.value)}
              />
              {c.modelo && (
                <button className="chip" onClick={() => { c.setModelo(null); setEligeModelo(false); }}>Usar el suyo</button>
              )}
              {catalogo
                .filter((m) => { const q = buscaModelo.trim().toLowerCase(); return !!q && (m.id.toLowerCase().includes(q) || m.nombre.toLowerCase().includes(q)); })
                .slice(0, 8)
                .map((m) => (
                  <button
                    key={m.id} className={"chip" + (m.id === modeloActual ? " puesto" : "")} title={`${m.proveedor} · ${m.id}`}
                    onClick={() => { c.setModelo(m.id); setEligeModelo(false); setBuscaModelo(""); }}
                  >
                    {sinPrefijo(m.nombre)} · {m.entrada ?? "—"} / {m.salida ?? "—"} $
                  </button>
                ))}
            </div>
          )}
          {eligeAlcance && caps && (
            <div className="adjuntos">
              <button className={"chip" + (!c.capitulo ? " puesto" : "")}
                      onClick={() => { c.setCapitulo(null); setEligeAlcance(false); }}>
                {c.capitulo ? "✓ " : ""}Todo el libro
              </button>
              {caps.map((cap) => (
                <button key={cap.title}
                        className={"chip" + (c.capitulo?.title === cap.title ? " puesto" : "")}
                        onClick={() => { c.setCapitulo({ title: cap.title, texto: cap.texto }); setEligeAlcance(false); }}>
                  {c.capitulo?.title === cap.title ? "✓ " : ""}{cap.title} · {cap.palabras} palabras
                </button>
              ))}
            </div>
          )}
          <textarea
            className="escribir"
            placeholder={`Dile a ${colaborador?.name ?? "tu colaborador"} qué necesitas…`}
            value={encargo}
            rows={2}
            onChange={(e) => setEncargo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) enviar(); }}
          />
          {/* Sin diálogo modal, sin fricción — una línea, dicha ANTES de pulsar enviar
              (DECISIONES.md §5). Si no se sabe el precio del modelo no se inventa una cifra: se
              calla en vez de arriesgar un número falso. */}
          {!!recortes.length && (
            <p className="vacio">
              Solo viajan las primeras {PALABRAS_MAX_POR_ADJUNTO.toLocaleString("es-ES")} palabras de {recortes.map((r) => nombreDeRuta(r.ruta)).join(", ")}.
            </p>
          )}
          {estimacion && estimacion.coste != null && (
            <p className="vacio">
              Esto son ~{estimacion.minutos < 1 ? `${Math.max(5, Math.round(estimacion.minutos * 60))} s` : `${Math.max(1, Math.round(estimacion.minutos))} min`}
              {" "}y ~{dolares(estimacion.coste)} de tu llave
              {/* Con «libro entero» los demás adjuntos también viajan enteros y su tamaño no se sabe aquí. */}
              {c.libroEntero && adjuntos.some((a) => a !== c.abierto?.ruta) ? ", más lo que ocupen enteros los otros adjuntos" : ""}.
            </p>
          )}
          <div className="pie">
            <button className={"chip" + (adjuntos.length ? " puesto" : "")}
                    onClick={() => setEligiendo((v) => !v)}>
              ＋ {adjuntos.length ? `${adjuntos.length} adjunto${adjuntos.length > 1 ? "s" : ""}` : "Adjuntar"}
            </button>
            {/* R-18: mandar los adjuntos enteros en vez de sus primeras 1.450 palabras. */}
            {camino === "conversacion" && (adjuntos.length > 0 || !!c.abierto) && (
              <button className={"chip" + (c.libroEntero ? " puesto" : "")} onClick={() => c.setLibroEntero(!c.libroEntero)}
                      title="Los documentos adjuntos viajan completos (hasta 500.000 caracteres). Cuesta más: la cifra de arriba lo dice.">
                {c.libroEntero ? "✓ " : ""}Libro entero
              </button>
            )}
            <label className="chip" style={{ cursor: "pointer" }}>
              {subiendo ? "Subiendo…" : "⇧ Subir documento"}
              <input
                type="file"
                multiple
                accept=".md,.txt,.markdown,.docx"
                style={{ display: "none" }}
                onChange={(e) => recibir(e.target.files)}
              />
            </label>
            {modeloActual && (
              <button
                className={"chip" + (c.modelo ? " puesto" : "")} onClick={() => setEligeModelo((v) => !v)}
                title={c.modelo ? "Solo para este encargo; después vuelve el del agente" : "El modelo de este agente (sección Modelos)"}
              >
                ◇ {nombreModelo(modeloActual)}
              </button>
            )}
            {/* Solo aparece si el documento abierto tiene capítulos de verdad que distinguir —
                sin al menos dos, restringir el alcance no significa nada. */}
            {c.abierto && caps && caps.length > 1 && capsFiables && (
              <button
                className={"chip" + (c.capitulo ? " puesto" : "")} onClick={() => setEligeAlcance((v) => !v)}
                title="Restringe el encargo a un capítulo en vez de al libro entero"
              >
                ◧ {c.capitulo ? c.capitulo.title : "Todo el libro"}
              </button>
            )}
            <span className="vacio" title={c.abierto?.ruta}>
              {c.abierto
                ? `sobre ${c.capitulo ? c.capitulo.title : c.abierto.ruta.split("/").pop()}`
                : "⌘↵ para enviar"}
            </span>
            <button
              className="enviar"
              aria-label="Enviar el encargo"
              disabled={!colaborador || !encargo.trim() || ocupado}
              onClick={() => enviar()}
            >↑</button>
          </div>
        </div>
      </div>
  );
}
